/**
 * PresupuestoAprobacionModal.jsx
 * Aprobación total o PARCIAL de un presupuesto (cantidad aprobada por renglón), con histórico de
 * aprobaciones y de facturas emitidas contra lo aprobado.
 */
import React, { useState, useEffect, useCallback } from "react";
import { X, CheckCircle2, Loader2, History, Receipt } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtFecha = (iso) => { try { return new Date(iso).toLocaleString("es-PY", { dateStyle: "short", timeStyle: "short" }); } catch { return iso || ""; } };
const fmtMonto = (n, m) => (m === "USD" ? `USD ${Number(n || 0).toLocaleString("es-PY", { minimumFractionDigits: 2 })}` : `₲ ${Math.round(Number(n || 0)).toLocaleString("es-PY")}`);

export default function PresupuestoAprobacionModal({ presupuestoId, token, canEdit, onClose, onSaved }) {
  const [pres, setPres] = useState(null);
  const [vals, setVals] = useState({});   // idx -> valor digitado (string): "sumar" = cantidad a aprobar ahora; "corregir" = total aprobado
  const [modo, setModo] = useState("sumar"); // "sumar" | "corregir"
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("aprobar");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/presupuestos/${presupuestoId}`, { headers });
      if (!r.ok) throw new Error("No se pudo cargar el presupuesto");
      const d = await r.json();
      setPres(d);
      setModo("sumar");
      setVals(Object.fromEntries((d.items || []).map((_, i) => [i, ""])));
    } catch (e) { toast.error(e.message); onClose(); }
    // eslint-disable-next-line
  }, [presupuestoId, token]);
  useEffect(() => { cargar(); }, [cargar]);

  if (!pres) {
    return (
      <div className="fixed inset-0 z-[260] bg-black/70 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
    );
  }

  const items = pres.items || [];
  // Cantidad ya aprobada de cada renglón (acumulada) antes de esta aprobación
  const yaDe = (it) => (it.cantidad_aprobada != null ? Number(it.cantidad_aprobada) : (Number(it.cantidad_facturada || 0) > 0 ? Number(it.cantidad) : 0));
  const saldoDe = (it) => Math.max(0, Number(it.cantidad || 0) - yaDe(it));
  // Total aprobado acumulado resultante de lo digitado
  const totalDe = (it, i) => {
    const v = Number(vals[i]);
    if (modo === "corregir") return vals[i] === "" || vals[i] == null ? yaDe(it) : v;
    return yaDe(it) + (vals[i] === "" || vals[i] == null ? 0 : v);
  };
  const aprobDe = (i) => totalDe(items[i], i);
  const totalCant = items.reduce((s, it) => s + Number(it.cantidad || 0), 0);
  const totalAprob = items.reduce((s, it, i) => s + (aprobDe(i) || 0), 0);
  const totalYa = items.reduce((s, it) => s + yaDe(it), 0);
  const invalido = items.some((it, i) => {
    const v = aprobDe(i);
    return isNaN(v) || v < 0 || v > Number(it.cantidad) || v < Number(it.cantidad_facturada || 0) || (modo === "sumar" && Number(vals[i] || 0) < 0);
  });
  // Monto aprobado: proporcional por renglón (subtotal ya incluye descuentos del renglón)
  const montoAprobado = items.reduce((s, it, i) => s + (Number(it.cantidad) > 0 ? Number(it.subtotal || 0) * (aprobDe(i) || 0) / Number(it.cantidad) : 0), 0);
  const cambiarModo = (m) => { setModo(m); setVals(Object.fromEntries(items.map((it, i) => [i, m === "corregir" ? String(yaDe(it)) : ""]))); };
  const aprobarSaldo = () => setVals(Object.fromEntries(items.map((it, i) => [i, String(modo === "corregir" ? Number(it.cantidad) : saldoDe(it))])));

  const guardar = async () => {
    if (invalido) { toast.error("Revisá las cantidades: no pueden superar lo presupuestado ni bajar de lo ya facturado"); return; }
    if (totalAprob <= 0) { toast.error("Aprobá al menos un renglón"); return; }
    if (modo === "sumar" && totalAprob === totalYa) { toast.error("Indicá cuánto aprobar ahora en al menos un renglón"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/presupuestos/${presupuestoId}/aprobar`, {
        method: "POST", headers,
        body: JSON.stringify({ items: items.map((_, i) => ({ idx: i, cantidad_aprobada: aprobDe(i) || 0 })), notas }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error al aprobar");
      toast.success(totalAprob === totalCant ? "Presupuesto aprobado (total)" : "Presupuesto aprobado parcialmente");
      onSaved?.(d);
      onClose();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="font-heading text-lg text-white">Aprobación del presupuesto {pres.numero}</h2>
            <p className="text-slate-400 text-sm">{pres.empresa_nombre} · Total presupuestado {fmtMonto(pres.total, pres.moneda)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-4 w-fit">
          {[["aprobar", "Aprobar"], ["historial", `Histórico (${(pres.aprobaciones || []).length + (pres.facturas_vinculadas || []).length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-lg text-sm ${tab === k ? "bg-orange-500 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>

        {tab === "aprobar" && (
          <>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <p className="text-xs text-slate-400 max-w-xl">
                {modo === "sumar"
                  ? "Indicá cuánto aprueba el cliente AHORA de cada renglón; se suma a lo ya aprobado y no puede superar el saldo del presupuesto. Al facturar o vincular se toma lo aprobado que todavía no se facturó."
                  : "Modo corrección: digitá el TOTAL aprobado de cada renglón (nunca por debajo de lo ya facturado)."}
              </p>
              {canEdit && (
                <div className="flex gap-1 bg-white/5 rounded-lg p-1 text-xs">
                  {[["sumar", "Aprobar más"], ["corregir", "Corregir total"]].map(([k, l]) => (
                    <button key={k} onClick={() => cambiarModo(k)} className={`px-3 py-1 rounded-md ${modo === k ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs text-slate-400">
                  <tr>
                    <th className="text-left p-2">Descripción</th><th className="p-2 text-center">Presup.</th><th className="p-2 text-center">Ya aprobado</th>
                    <th className="p-2 text-center w-28">{modo === "sumar" ? "Aprobar ahora" : "Total aprobado"}</th>
                    <th className="p-2 text-center">Facturado</th><th className="p-2 text-center">A facturar</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const total = aprobDe(i) || 0;
                    const pend = Math.max(0, total - Number(it.cantidad_facturada || 0));
                    return (
                      <tr key={i} className="border-t border-white/5 text-slate-200">
                        <td className="p-2">{it.descripcion}{it.variante_texto && <span className="text-xs text-emerald-400"> · {it.variante_texto}</span>}</td>
                        <td className="p-2 text-center">{it.cantidad}</td>
                        <td className="p-2 text-center text-slate-400">{yaDe(it)}</td>
                        <td className="p-2 text-center">
                          <input type="number" step="1" disabled={!canEdit}
                            min={modo === "corregir" ? Number(it.cantidad_facturada || 0) : 0} max={modo === "corregir" ? it.cantidad : saldoDe(it)}
                            placeholder={modo === "sumar" ? `máx ${saldoDe(it)}` : ""}
                            value={vals[i] ?? ""} onChange={e => setVals({ ...vals, [i]: e.target.value })}
                            className="w-24 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-center text-white focus:outline-none focus:border-orange-400" />
                        </td>
                        <td className="p-2 text-center text-slate-400">{Number(it.cantidad_facturada || 0)}</td>
                        <td className="p-2 text-center font-medium text-orange-300">{pend}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm">
              <span className="text-slate-300">Aprobado acumulado: <b className="text-white">{totalAprob}</b> de {totalCant} unidades (ya aprobado {totalYa}) · <b className="text-white">{fmtMonto(montoAprobado, pres.moneda)}</b></span>
              {canEdit && (
                <button className="text-xs text-orange-300 underline" onClick={aprobarSaldo}>{modo === "sumar" ? "Aprobar todo el saldo" : "Aprobar todo"}</button>
              )}
            </div>
            <textarea rows={2} disabled={!canEdit} value={notas} onChange={e => setNotas(e.target.value)} placeholder="Notas de la aprobación (opcional): quién aprobó, referencia del cliente…"
              className="w-full mt-3 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-400" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-slate-300">Cerrar</button>
              {canEdit && (
                <button onClick={guardar} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm text-white">
                  <CheckCircle2 className="w-4 h-4" /> {saving ? "Guardando..." : totalAprob === totalCant ? "Aprobar todo" : "Aprobar parcialmente"}
                </button>
              )}
            </div>
          </>
        )}

        {tab === "historial" && (
          <div className="space-y-5">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Aprobaciones</p>
              {(pres.aprobaciones || []).length === 0 && <p className="text-sm text-slate-500">Todavía no hay aprobaciones registradas.</p>}
              {[...(pres.aprobaciones || [])].reverse().map(a => (
                <div key={a.id} className="border border-white/10 rounded-lg p-3 mb-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span><b className={a.tipo === "total" ? "text-emerald-400" : "text-amber-300"}>{a.tipo === "total" ? "Aprobación total" : "Aprobación parcial"}</b> · {a.total_aprobado}/{a.total_presupuestado} u.</span>
                    <span className="text-xs text-slate-500">{fmtFecha(a.fecha)} · {a.usuario}</span>
                  </div>
                  {a.notas && <p className="text-xs text-slate-400 mt-1">{a.notas}</p>}
                  <ul className="mt-1 text-xs text-slate-400 space-y-0.5">
                    {(a.items || []).map((c, k) => <li key={k}>{c.descripcion}: {c.antes} → <b className="text-slate-200">{c.despues}</b> de {c.cantidad}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Receipt className="w-3 h-3" /> Facturas contra este presupuesto</p>
              {(pres.facturas_vinculadas || []).length === 0 && <p className="text-sm text-slate-500">Sin facturas vinculadas.</p>}
              {(pres.facturas_vinculadas || []).map(f => (
                <div key={f.id} className="border border-white/10 rounded-lg p-3 mb-2 text-sm text-slate-300">
                  <div className="flex justify-between"><span><b>{f.numero}</b> · {f.fecha}</span><span>{fmtMonto(f.monto, f.moneda)} · {f.estado}</span></div>
                  {f.sin_detalle
                    ? <p className="text-xs text-slate-500 mt-1">Vinculada sin detalle por renglón (se toma como facturación completa de lo aprobado).</p>
                    : <ul className="mt-1 text-xs text-slate-400">{(f.items || []).map((c, k) => <li key={k}>{c.descripcion || `Renglón ${c.idx + 1}`}: {c.cantidad}</li>)}</ul>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
